import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, Save, LogOut, FileText, Loader2, ArrowLeft, Lock, Copy, ExternalLink, Trash2, Star } from "lucide-react";
import { api, formatApiErrorDetail, agreementDownloadUrl, formatBytes } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const Card = ({ title, children }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm" data-testid="admin-card">
    <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy">{title}</h2>
    <div className="mt-5">{children}</div>
  </div>
);

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [contact, setContact] = useState({ phone: "", email: "", claims_line: "", address: "" });
  const [savingContact, setSavingContact] = useState(false);
  const [url, setUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [quotes, setQuotes] = useState([]);

  const fmtUsd = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(
      Number(n) || 0,
    );

  useEffect(() => {
    if (user) {
      api
        .get("/admin/quotes")
        .then((r) => setQuotes(r.data))
        .catch(() => {});
    }
  }, [user]);

  const [files, setFiles] = useState([]);
  const [fileUpload, setFileUpload] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [setupId, setSetupId] = useState("");

  const loadFiles = () =>
    api
      .get("/admin/files")
      .then((r) => setFiles(r.data))
      .catch(() => {});

  useEffect(() => {
    if (user) {
      loadFiles();
      api
        .get("/settings")
        .then((r) => setSetupId(r.data.setup_file_short_id || ""))
        .catch(() => {});
    }
  }, [user]);

  const setSetupFile = async (shortId) => {
    try {
      await api.put("/admin/settings", { setup_file_short_id: shortId });
      setSetupId(shortId);
      toast.success("Setup file updated — /download now serves this file");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user) {
      loadFiles();
      api
        .get("/settings")
        .then((r) => setSetupId(r.data.setup_file_short_id || ""))
        .catch(() => {});
      api
        .get("/admin/stats")
        .then((r) => setStats(r.data))
        .catch(() => {});
    }
  }, [user]);

  const uploadFile = async () => {
    if (!fileUpload) {
      toast.error("Choose a file first");
      return;
    }
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("file", fileUpload);
      await api.post("/admin/files/upload", fd);
      setFileUpload(null);
      await loadFiles();
      toast.success("File uploaded — direct link ready");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (shortId) => {
    await api.delete(`/admin/files/${shortId}`).catch(() => {});
    await loadFiles();
    toast.success("File removed");
  };

  const copyLink = async (shortId) => {
    const link = `${window.location.origin}/f/${shortId}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Direct link copied");
    } catch {
      toast.error(link);
    }
  };

  const loadSettings = () =>
    api
      .get("/settings")
      .then((r) => {
        setSettings(r.data);
        setContact({
          phone: r.data.phone || "",
          email: r.data.email || "",
          claims_line: r.data.claims_line || "",
          address: r.data.address || "",
        });
      })
      .catch(() => {});

  useEffect(() => {
    loadSettings();
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      toast.success("Welcome back");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setEmail("");
    setPassword("");
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      const { data } = await api.put("/admin/settings", contact);
      setSettings(data);
      toast.success("Contact details saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingContact(false);
    }
  };

  const saveUrl = async () => {
    setSavingUrl(true);
    try {
      const { data } = await api.put("/admin/settings", { agreement_url: url });
      setSettings(data);
      toast.success("Agreement link updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingUrl(false);
    }
  };

  const upload = async () => {
    if (!file) {
      toast.error("Choose a PDF, DOC or DOCX file first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/agreement/upload", fd);
      setSettings(data);
      setFile(null);
      toast.success("Agreement file uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-paper">
        <Loader2 className="h-6 w-6 animate-spin text-brand-blue" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-paper px-4">
        <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5" data-testid="admin-login-card">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-brand-navy">Fleetguard Admin</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">Restricted access</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-7 space-y-4">
            <div>
              <label htmlFor="admin-email" className="mb-1.5 block text-sm font-semibold text-brand-navy">Email</label>
              <Input
                id="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sales@leetguard-ins.com"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-1.5 block text-sm font-semibold text-brand-navy">Password</label>
              <Input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="admin-login-password"
              />
            </div>
            {loginError && (
              <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <Button
              type="submit"
              disabled={loggingIn}
              className="w-full rounded-full bg-brand-blue py-3 text-base font-semibold text-white hover:bg-brand-bluedeep"
              data-testid="admin-login-submit"
            >
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              Sign in
            </Button>
          </form>
          <a href="/" data-testid="admin-back-to-site" className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-brand-blue">
            <ArrowLeft className="h-4 w-4" /> Back to website
          </a>
        </div>
      </div>
    );
  }

  const agreement = settings?.agreement || { source: "default", filename: "Fleetguard-Client-Agreement.pdf", size: 0 };

  return (
    <div className="min-h-screen bg-brand-paper py-10">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-9" />
            <div>
              <h1 className="font-display text-lg font-bold tracking-tight text-brand-navy">Admin Panel</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              data-testid="admin-view-site"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-brand-navy hover:border-brand-blue hover:text-brand-blue"
            >
              <ArrowLeft className="h-4 w-4" /> View site
            </a>
            <Button
              variant="outline"
              onClick={logout}
              data-testid="admin-logout"
              className="rounded-full border-slate-300 bg-white text-brand-navy hover:bg-slate-50"
            >
              <LogOut className="h-4 w-4" /> Log out
            </Button>
          </div>
        </div>

        <div className="mt-8 space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="admin-stats">
            {[
              { label: "Agreement downloads", value: stats?.agreement_downloads ?? 0, tid: "admin-stat-agreement" },
              { label: "Setup downloads", value: stats?.setup_downloads ?? 0, tid: "admin-stat-setup" },
              { label: "Quote requests", value: stats?.quote_requests ?? 0, tid: "admin-stat-quotes" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={s.tid}>
                <p className="font-display text-3xl font-extrabold text-brand-blue">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
          <Card title="Client Agreement">
            <div className="flex items-center gap-4 rounded-2xl bg-brand-ice p-4" data-testid="admin-agreement-status">
              <FileText className="h-8 w-8 shrink-0 text-brand-blue" />
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-brand-navy" data-testid="admin-agreement-filename">
                  {agreement.filename}
                  {agreement.size ? ` · ${formatBytes(agreement.size)}` : ""}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500" data-testid="admin-agreement-source">
                  {agreement.source === "uploaded"
                    ? "Uploaded file"
                    : agreement.source === "url"
                    ? "External link"
                    : "Built-in sample PDF"}
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <label className="block text-sm font-semibold text-brand-navy">
                Option A — upload the agreement file (any file type · max 500 MB)
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="admin-agreement-upload-input"
                  className="flex-1 cursor-pointer"
                />
                <Button
                  onClick={upload}
                  disabled={uploading}
                  data-testid="admin-agreement-upload-btn"
                  className="rounded-full bg-brand-blue px-6 font-semibold text-white hover:bg-brand-bluedeep"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
                </Button>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>

              <label className="block text-sm font-semibold text-brand-navy">
                Option B — link to a hosted file (any direct file URL)
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  type="url"
                  placeholder="https://your-storage.com/agreement.pdf"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="admin-agreement-url-input"
                  className="flex-1"
                />
                <Button
                  onClick={saveUrl}
                  disabled={savingUrl}
                  data-testid="admin-agreement-url-save"
                  className="rounded-full bg-brand-navy px-6 font-semibold text-white hover:bg-brand-ink"
                >
                  {savingUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Save link
                </Button>
              </div>
              <p className="text-xs text-slate-400">
                Leave the field empty and save to stop using an external link.{" "}
                <a href={agreementDownloadUrl} data-testid="admin-agreement-test-download" className="text-brand-blue underline">
                  Test the public download
                </a>
              </p>
            </div>
          </Card>

          <Card title="Contact Details">
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { key: "phone", label: "Phone", ph: "(555) 210-4400" },
                { key: "email", label: "Email", ph: "quotes@fleetguardinsurance.com" },
                { key: "claims_line", label: "24/7 Claims Line", ph: "1-800-555-0187" },
              ].map((f) => (
                <div key={f.key}>
                  <label htmlFor={`admin-contact-${f.key}`} className="mb-1.5 block text-sm font-semibold text-brand-navy">
                    {f.label}
                  </label>
                  <Input
                    id={`admin-contact-${f.key}`}
                    value={contact[f.key]}
                    onChange={(e) => setContact({ ...contact, [f.key]: e.target.value })}
                    placeholder={f.ph}
                    data-testid={`admin-contact-${f.key}`}
                  />
                </div>
              ))}
              <div>
                <label htmlFor="admin-contact-address" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Office Address
                </label>
                <Input
                  id="admin-contact-address"
                  value={contact.address}
                  onChange={(e) => setContact({ ...contact, address: e.target.value })}
                  placeholder="1420 Freight Lane, Suite 300, Dallas, TX 75207"
                  data-testid="admin-contact-address"
                />
              </div>
            </div>
            <Button
              onClick={saveContact}
              disabled={savingContact}
              data-testid="admin-contact-save"
              className="mt-5 rounded-full bg-brand-blue px-6 font-semibold text-white hover:bg-brand-bluedeep"
            >
              {savingContact ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save contact details
            </Button>
          </Card>

          <Card title={`Quote Requests${quotes.length ? ` (${quotes.length})` : ""}`}>
            {quotes.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="admin-quotes-empty">
                No quote requests yet — new submissions from the website appear here automatically.
              </p>
            ) : (
              <div className="space-y-3" data-testid="admin-quotes-list">
                {quotes.map((q, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 p-4" data-testid={`admin-quote-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-bold text-brand-navy">
                        {q.contact_name}
                        <span className="font-medium text-slate-600"> · {q.company_name}</span>
                      </p>
                      <span className="rounded-full bg-emerald-50 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-emerald-600">
                        {q.status || "new"}
                      </span>
                    </div>
                    <div className="mt-3 grid gap-x-4 gap-y-1 text-sm text-slate-600 sm:grid-cols-2">
                      <p data-testid={`admin-quote-fleet-${i}`}>Fleet: {q.fleet_size}</p>
                      <p data-testid={`admin-quote-trucks-${i}`}>Trucks: {q.trucks}</p>
                      <p data-testid={`admin-quote-invoice-${i}`}>Last invoice: {fmtUsd(q.last_invoice)}/mo</p>
                      <p data-testid={`admin-quote-contact-${i}`}>
                        {q.email}
                        {q.phone ? ` · ${q.phone}` : ""}
                      </p>
                    </div>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {new Date(q.created_at).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title={`Files & Direct Links${files.length ? ` (${files.length})` : ""}`}>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                type="file"
                onChange={(e) => setFileUpload(e.target.files?.[0] || null)}
                data-testid="admin-files-upload-input"
                className="flex-1 cursor-pointer"
              />
              <Button
                onClick={uploadFile}
                disabled={uploadingFile}
                data-testid="admin-files-upload-btn"
                className="rounded-full bg-brand-blue px-6 font-semibold text-white hover:bg-brand-bluedeep"
              >
                {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              </Button>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Any file type, up to 500 MB. Every file gets a direct download link you can share with anyone. Mark one
              file as the <span className="font-semibold text-slate-500">Setup file</span> and it's served at{" "}
              <span className="font-mono text-slate-500">/download</span>.
            </p>
            {files.length > 0 && (
              <div className="mt-5 space-y-3" data-testid="admin-files-list">
                {files.map((f, i) => (
                  <div key={f.short_id} className="rounded-2xl border border-slate-200 p-4" data-testid={`admin-file-${i}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="min-w-0 truncate text-sm font-bold text-brand-navy" data-testid={`admin-file-name-${i}`}>
                        {f.name} <span className="font-normal text-slate-400">· {formatBytes(f.size)}</span>
                  {f.download_count ? (
                    <span className="font-normal text-slate-400" data-testid={`admin-file-count-${i}`}>
                      {" "}
                      · {f.download_count} download{f.download_count === 1 ? "" : "s"}
                    </span>
                  ) : null}
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        {setupId === f.short_id ? (
                          <span
                            className="inline-flex h-8 items-center gap-1 rounded-full border border-blue-200 bg-brand-ice px-3 text-sm font-semibold text-brand-blue"
                            data-testid={`admin-file-setup-badge-${i}`}
                          >
                            <Star className="h-3.5 w-3.5" /> Setup file
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => setSetupFile(f.short_id)}
                            data-testid={`admin-file-set-setup-${i}`}
                            className="h-8 rounded-full border-slate-300 px-3 text-brand-navy hover:bg-slate-50"
                          >
                            <Star className="h-3.5 w-3.5" /> Set as setup
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          onClick={() => copyLink(f.short_id)}
                          data-testid={`admin-file-copy-${i}`}
                          className="h-8 rounded-full border-slate-300 px-3 text-brand-navy hover:bg-slate-50"
                        >
                          <Link2 className="h-3.5 w-3.5" /> Copy link
                        </Button>
                        <a
                          href={`/api/files/${f.short_id}`}
                          data-testid={`admin-file-open-${i}`}
                          className="inline-flex h-8 items-center gap-1 rounded-full border border-slate-300 px-3 text-sm font-semibold text-brand-navy transition-colors hover:border-brand-blue hover:text-brand-blue"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Open
                        </a>
                        <Button
                          variant="outline"
                          onClick={() => deleteFile(f.short_id)}
                          data-testid={`admin-file-delete-${i}`}
                          className="h-8 rounded-full border-slate-300 px-3 text-red-500 hover:bg-red-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <p className="mt-2 break-all font-mono text-[11px] text-slate-400" data-testid={`admin-file-url-${i}`}>
                      {window.location.origin}/f/{f.short_id}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
